import { Icon, Skeleton, Stack, Typography, styled } from '@mui/material';
import { MessageStatus, MessagesForSchoolCountsResponse } from '@schooly/api';
import { Counter, MailIcon, PendingMessageIcon, SimpleCard, ThreadIcon } from '@schooly/style';
import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessagesWidgetData } from './useMessagesWidgetData';

const MessageCard = styled(SimpleCard)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  '.card-actions': { minWidth: 0 },
  '&:hover': {
    '.MuiCardActionArea-focusHighlight': { background: 'transparent' },
    '.MuiCardActionArea-root': { background: theme.palette.common.lightBg },
  },
}));

type MessageCardItem = {
  titleId: string;
  subtitleId: string;
  iconColor: string;
  iconBgColor: string;
  counter?: number;
  status: MessageStatus[];
  icon: ReactNode;
};

const getCards = (counts?: MessagesForSchoolCountsResponse['counts']): MessageCardItem[] => [
  {
    titleId: 'dashboard-teacher-Messages-AllSent',
    subtitleId: 'dashboard-teacher-Messages-AllSent-subtitle',
    iconColor: 'success.main',
    iconBgColor: 'success.superLight',
    status: [MessageStatus.Published, MessageStatus.Draft, MessageStatus.Submitted],
    icon: <ThreadIcon />,
  },
  {
    titleId: 'dashboard-teacher-Messages-AwaitingApproval',
    subtitleId: 'dashboard-teacher-Messages-AwaitingApproval-subtitle',
    iconColor: 'common.orange',
    iconBgColor: 'common.orange5',
    counter: counts?.submitted ?? 0,
    status: [MessageStatus.Submitted],
    icon: <PendingMessageIcon />,
  },
  {
    titleId: 'dashboard-teacher-Messages-MyDrafts',
    subtitleId: 'dashboard-teacher-Messages-MyDrafts-subtitle',
    iconColor: 'common.grey3',
    iconBgColor: 'common.lightBg',
    counter: counts?.draft ?? 0,
    status: [MessageStatus.Draft],
    icon: <MailIcon />,
  },
];

const SKELETON_CARDS = 3;

export const MessagesWidget: FC = () => {
  const { counts, isLoading, handleNavigate } = useMessagesWidgetData();

  return (
    <Stack gap={0.75} pt={1.75}>
      {isLoading
        ? Array.from({ length: SKELETON_CARDS }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" sx={{ minHeight: 64, borderRadius: 1 }} />
          ))
        : getCards(counts).map((card) => (
            <MessageCard
              key={card.titleId}
              withBorder
              onItemClick={() => handleNavigate(card.status)}
              actionAreaProps={{ sx: { p: 1.5 } }}
              actions={
                card.counter !== undefined ? (
                  <Stack justifyContent="center" height="100%">
                    <Counter inverse size="sm">
                      {card.counter}
                    </Counter>
                  </Stack>
                ) : undefined
              }
            >
              <Stack direction="row" alignItems="center" gap={1} flex={1}>
                <Icon
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '100px',
                    bgcolor: card.iconBgColor,
                    color: card.iconColor,
                  }}
                >
                  {card.icon}
                </Icon>
                <Stack>
                  <Typography variant="h3">
                    <FormattedMessage id={card.titleId} />
                  </Typography>
                  <Typography variant="body1" color="common.grey3">
                    <FormattedMessage id={card.subtitleId} />
                  </Typography>
                </Stack>
              </Stack>
            </MessageCard>
          ))}
    </Stack>
  );
};
